import { getFromPath } from 'src/helpers/utils';

export type SeoMeta = {
  title?: string;
  description?: string;
  keywords?: string;
};

export type CmsMeta = {
  pageTitle?: string;
  pageDescription?: string;
  pageKeywords?: string;
};

export type SeoMetaConfig = {
  categories?: Record<string, SeoMeta>;
};

export type SeoState = {
  category: string;
  meta: SeoMeta;
  canonicalUrl: string;
};

const normalize = (category: string): string =>
  category
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase();

export const getCanonicalUrl = (pathname: string): string => {
  if (typeof window === 'undefined') return pathname;
  try {
    return new URL(pathname, window.location.origin).toString();
  } catch {
    return pathname;
  }
};

export const toSeoMeta = (meta?: CmsMeta | null): SeoMeta => ({
  title: meta?.pageTitle,
  description: meta?.pageDescription,
  keywords: meta?.pageKeywords,
});

export const hasSeoMeta = (meta?: SeoMeta | null): boolean =>
  Boolean(meta?.title || meta?.description || meta?.keywords);

const getCategoryCandidates = (pathname: string, fallbackCategory: string): string[] => {
  const firstSegment = getFromPath(pathname, '/', 1);

  if (!firstSegment) return fallbackCategory ? [fallbackCategory] : [];

  if (firstSegment === 'category' || firstSegment === 'content') {
    const secondSegment = getFromPath(pathname, '/', 2);
    return secondSegment ? [secondSegment] : [];
  }

  return [firstSegment];
};

const parseConfig = (rawConfig?: string): SeoMetaConfig | null => {
  if (!rawConfig) return null;

  try {
    return JSON.parse(rawConfig);
  } catch {
    return null;
  }
};

export const getSeoState = (pathname: string, fallbackCategory: string, rawConfig?: string): SeoState => {
  const config = parseConfig(rawConfig);

  const candidates = getCategoryCandidates(pathname, fallbackCategory);

  let category = candidates[0] ?? '';
  let meta: SeoMeta = {};

  for (const candidate of candidates) {
    const normalized = normalize(candidate);

    const found = config?.categories?.[normalized] || config?.categories?.[candidate];

    if (found) {
      category = candidate;
      meta = found;
      break;
    }
  }

  return {
    category,
    meta,
    canonicalUrl: getCanonicalUrl(pathname),
  };
};
